import React, { useState, useEffect } from "react";
import PublicLayout from "../../components/layout/PublicLayout";
import { Videos } from "../../components/shared";
import { VideoPlayer } from "../../components/shared";
import { generateClient } from "aws-amplify/data";
import { getUrl } from "aws-amplify/storage";
import { fetchAuthSession } from "aws-amplify/auth";

const client = generateClient();

async function pickAuthMode() {
  try {
    const s = await fetchAuthSession();
    return s.tokens ? "userPool" : "identityPool";
  } catch {
    return "identityPool";
  }
}

export default function Conferencias() {
  const [videos, setVideos] = useState([]);
  const [filteredVideos, setFilteredVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [tags, setTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState("Todos");
  const [loading, setLoading] = useState(true);

  // Obtener videos desde la base de datos
  useEffect(() => {
    const fetchVideos = async () => {
      try {
        setLoading(true);
        const authMode = await pickAuthMode();
        // Obtener lista de videos desde la base de datos
        const { data: videoItems } = await client.models.Conferencia.list({
          authMode,
        });
        const fetchedVideos = await Promise.all(
          videoItems.map(async (video) => {
            const { url: thumbnailUrl } = await getUrl({
              path: video.thumbnailPath,
            });
            const videoUrl = video.isFree
              ? (await getUrl({ path: video.videoPath })).url.href
              : video.videoPath;

            return {
              ...video,
              thumbnail: thumbnailUrl.href,
              videoUrl, // Las rutas privadas se firman tras validar la compra.
            };
          }),
        );

        setVideos(fetchedVideos);
        setFilteredVideos(fetchedVideos);

        // Obtener etiquetas únicas de todos los videos
        const uniqueTags = [
          "Todos",
          ...new Set(
            fetchedVideos.flatMap((video) =>
              video.tags.split(",").map((tag) => tag.trim()),
            ),
          ),
        ];
        setTags(uniqueTags);
      } catch (error) {
        console.error("Error al cargar los videos:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  // Manejar la selección de video
  const handleVideoClick = async (video) => {
    if (!video.isFree) {
      const hasAccess = await validateAccess(video.id); // Validar acceso para videos privados
      if (!hasAccess) {
        alert("Este video es privado. Debes realizar el pago para acceder.");
        return;
      }

      // Crear URL firmada para videos privados
      try {
        const { url } = await getUrl({ path: video.videoUrl });
        video.videoUrl = url.href;
      } catch (error) {
        console.error("Error al generar URL firmada:", error);
        alert("No se pudo generar acceso al video.");
        return;
      }
    }
    setSelectedVideo(video);
  };

  // Validar acceso para videos privados
  const validateAccess = async (videoId) => {
    try {
      const user = await client.auth.getCurrentUser();
      const { data: purchase } = await client.models.ConferenciaCompra.get({
        userId: user.id,
        conferenciaId: videoId,
      });
      return !!purchase;
    } catch (error) {
      console.error("Error al validar acceso:", error);
      return false;
    }
  };

  // Manejar el cambio de filtro
  const handleFilterChange = (tag) => {
    setSelectedTag(tag);
    if (tag === "Todos") {
      setFilteredVideos(videos);
    } else {
      setFilteredVideos(videos.filter((video) => video.tags.includes(tag)));
    }
  };

  // Función para volver a la galería
  const handleClosePlayer = () => {
    setSelectedVideo(null);
  };

  return (
    <PublicLayout>
      <div className="min-h-screen bg-gray-300 p-4 pt-10 md:px-10 lg:px-20">
        <div className="mb-4 rounded-lg bg-sky-400 py-2 text-center text-xl font-semibold text-sky-900 sm:text-2xl md:text-3xl lg:text-4xl">
          {selectedVideo ? "Reproducción de Video" : "Galería de Videos"}
        </div>

        {/* Filtros */}
        {!selectedVideo && (
          <div className="mb-6 flex flex-wrap justify-center gap-4">
            {tags.map((tag) => (
              <button
                key={tag}
                onClick={() => handleFilterChange(tag)}
                className={`rounded px-4 py-2 ${
                  selectedTag === tag ? "bg-blue-500 text-white" : "bg-gray-300"
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
        )}

        {/* Videos o Reproductor */}
        {selectedVideo ? (
          <div className="mx-auto max-w-4xl p-4">
            <VideoPlayer
              videoUrl={selectedVideo.videoUrl}
              title={selectedVideo.title}
              description={selectedVideo.description}
              views={selectedVideo.views}
              onClose={handleClosePlayer}
            />
          </div>
        ) : (
          <Videos videos={filteredVideos} onVideoClick={handleVideoClick} />
        )}
      </div>
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-blue-500 border-t-transparent"></div>
        </div>
      )}
    </PublicLayout>
  );
}
